import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import {
    CButton, CCard, CCardBody, CCardHeader, CForm, CFormInput, CFormSelect, CFormCheck, CCardFooter, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell, CFormFeedback, CSpinner
} from '@coreui/react';

import { apiClient } from 'services/Api';
import DateInput from 'components/forms/DateInput';
import { jwtDecode } from 'jwt-decode';
import TextEditor from "components/forms/TextEditor";
import ToastNotification from "components/globals/ToastNotification";
import { triggerAlert } from 'components/globals/AlertNotification';
import UserSelectionModal from "components/UserSelectionModal";

const Supplier = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(null);
    const location = useLocation();
    const [toast, setToast] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const token = localStorage.getItem("access_token");
    const decodedToken = jwtDecode(token);
    const is_manager = decodedToken.is_manager;

    const fetchSupplierDetails = async () => {
        let data = null; // Declare a variable to store API response
        try {
            const response = await apiClient.get(`/suppliers/${id}/`);
            data = response.data; // Store response data
            setFormData(data); // Update state
        } catch (error) {
            triggerAlert('Forenecedor não foi encontrado', 'error');
            navigate("/fornecedores");
        }
    };

    useEffect(() => {
        if (!id) {
            setFormData({});
        } else {
            fetchSupplierDetails();
        }
    }, [id, location.pathname]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
    
        let updatedValue;
        if (type === "checkbox") {
            updatedValue = checked;
        } else if (value === "true") {
            updatedValue = true;
        } else if (value === "false") {
            updatedValue = false;
        } else if (value === "") {
            updatedValue = null;
        } else {
            updatedValue = value;
        }
    
        setFormData((prevFormData) => {
            const updatedFormData = {
                ...prevFormData,
                [name]: updatedValue,
            };
    
            return updatedFormData;
        });
    
        setFieldErrors((prevErrors) => {
            const updated = { ...prevErrors };
            delete updated[name];
            return updated;
        });    
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (id) {
                await apiClient.put(`/suppliers/${id}/`, formData);
                triggerAlert('Fornecedor atualizado com sucesso!', 'success');
                navigate(`/fornecedores`);
            } else {
                await apiClient.post('/suppliers/', formData);
                triggerAlert('Fornecedor atualizado com sucesso!', 'success');
                navigate(`/fornecedores`);
            }
        } catch (error) {
            const errors = error.response?.data;
            let errorMessage = 'Erro ao gravar';

            if (errors && typeof errors === 'object') {
                setFieldErrors(errors); // <-- Save the full error object
            } else {
                setFieldErrors({});
            }

            setTimeout(() => {
                const firstInvalidInput = document.querySelector('input.is-invalid, textarea.is-invalid, select.is-invalid');
                if (firstInvalidInput) {
                    firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    firstInvalidInput.focus();
                }
            }, 50);


            setToast({ title: 'Fornecedor', message: errorMessage, type: 'error' });
            console.log('Error submitting form:', error);
            setLoading(false);
        }
    };


    if (formData !== null) {
        return (
            <div className="container mt-4">
                {/* Error Toast - Appears on submit failure */}
                <ToastNotification toast={toast} setToast={setToast} />

                <CCard>
                    <CCardHeader>
                        <h4>Fornecedor</h4>
                    </CCardHeader>
                    <CCardBody>
                        <CForm onSubmit={handleSubmit}>
                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Nome</strong>}
                                    name="name"
                                    value={formData.name ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.name}
                                />
                                {fieldErrors.name && (
                                    <CFormFeedback invalid>{fieldErrors.name[0]}</CFormFeedback>
                                )}
                            </div>

                            <DateInput
                                label={<strong>Data de Início</strong>}
                                name="start_date"
                                value={formData.start_date ?? ''}
                                onChange={handleInputChange}
                            />

                            <div className="mb-3">
                                <CFormInput
                                    type="text"
                                    label={<strong>Email</strong>}
                                    name="email"
                                    value={formData.email ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.email}
                                />
                                {fieldErrors.email && (
                                    <CFormFeedback invalid>{fieldErrors.email[0]}</CFormFeedback>
                                )}
                            </div>

                            <div className="mb-3">
                                <CFormSelect
                                    label={<strong>Categoria</strong>}
                                    name="category"
                                    value={formData.category ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.category}
                                >
                                    <option value="">Selecione</option>
                                    <option value="Agência de Viagens">Agência de Viagens</option>
                                    <option value="Comunicação">Comunicação</option>
                                    <option value="Contabilidade/Financeiro">Contabilidade/Financeiro</option>
                                    <option value="Formação">Formação</option>
                                    <option value="HST">HST</option>
                                    <option value="Informática">Informática</option>
                                    <option value="Qualidade">Qualidade</option>
                                    <option value="Seguros">Seguros</option>
                                    <option value="Serviços Jurídicos">Serviços Jurídicos</option>
                                    <option value="Telecomunicações">Telecomunicações</option>
                                    <option value="Outros">Outros</option>
                                </CFormSelect>
                                {fieldErrors.category && (
                                    <CFormFeedback invalid>{fieldErrors.category[0]}</CFormFeedback>
                                )}
                            </div>
                            
                            <div className="d-flex align-items-center mb-3">
                                <label htmlFor="critical" className="me-2 mb-0">
                                    <strong>Crítico</strong>
                                </label>
                                <CFormCheck
                                    id="critical"
                                    name="critical"
                                    checked={formData.critical}
                                    onChange={handleInputChange}
                                    label=""
                                />
                            </div>

                            <div className="mb-3">
                                <CFormSelect
                                    label={<strong>Situação</strong>}
                                    name="situation"
                                    value={formData.situation ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.situation}
                                >
                                    <option value="">Selecione</option>
                                    <option value="Fornecedor">Fornecedor</option>
                                    <option value="Novo Fornecedor">Novo Fornecedor</option>
                                    <option value="Potencial Fornecedor">Potencial Fornecedor</option>
                                </CFormSelect>
                                {fieldErrors.situation && (
                                    <CFormFeedback invalid>{fieldErrors.situation[0]}</CFormFeedback>
                                )}
                            </div>

                            <div className="mb-3">
                                <CFormSelect
                                    label={<strong>Tipo de Serviço</strong>}
                                    name="service_type"
                                    value={formData.service_type ?? ''}
                                    onChange={handleInputChange}
                                    className="mb-3"
                                    invalid={!!fieldErrors.service_type}
                                >
                                    <option value="">Selecione</option>
                                    <option value="Serviço Especializado">Serviço Especializado</option>
                                    <option value="Serviço Básico">Serviço Básico</option>
                                </CFormSelect>
                                {fieldErrors.service_type && (
                                    <CFormFeedback invalid>{fieldErrors.service_type[0]}</CFormFeedback>
                                )}
                            </div>
                        </CForm>
                    </CCardBody>
                    <CCardFooter className="d-flex justify-content-end gap-2">
                        <CButton color="danger" className="text-white" onClick={() => navigate('/fornecedores')}>
                            Cancelar
                        </CButton>
                        <CButton disabled={loading} color="success" className="text-white" type="submit" onClick={handleSubmit}>
                            {loading ? <CSpinner size="sm" /> : 'Confirmar'}
                        </CButton>
                    </CCardFooter>
                </CCard>
            </div>
        );
    }

    return (
        <div className="container mt-4">
            <h2>Loading...</h2>
        </div>
    );
};

export default Supplier;
